import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';

/**
 * Process the state parameter to determine where to redirect after login
 * @param {string | null} stateParam - The state parameter from OAuth callback
 * @returns {string} The URL to redirect to
 */
const processRedirectState = (stateParam: string | null): string => {
  if (!stateParam) return '/explore';
  
  try {
    const stateObj = JSON.parse(stateParam);
    
    if (stateObj.redirectType === 'chat' && stateObj.characterUsername) {
      // Redirect to the character
      return `/chars/${stateObj.characterUsername}?autostart=true`;
    } else if (stateObj.redirectType === 'scenario' && stateObj.scenarioDefinitionId) {
      // Redirect to the scenario
      return `/scenarios/${stateObj.scenarioDefinitionId}?autostart=true`;
    }
  } catch (error) {
    console.error('Error parsing state parameter:', error);
  }
  
  // Default redirect
  return '/explore';
};

export function GoogleOAuthCallback(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const { setIsLoggedIn, setUser } = useAuth();

  useEffect(() => {
    const handleCallback = async (): Promise<void> => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const error = params.get('error');
      const state = params.get('state');
      
      if (error) {
        console.error('Authorization error:', error);
        setError(error);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (!code) {
        console.error('No authorization code received');
        setError('No authorization code received');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        await api.auth.login(code, 'google');
        // Fetch user data after successful login
        const userData = await api.user.get();
        setUser(userData);
        setIsLoggedIn(true);
        
        // Process redirect based on state parameter
        const redirectUrl = processRedirectState(state);
        navigate(redirectUrl);
      } catch (error) {
        console.error('Authentication failed:', error);
        const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
        setError(errorMessage);
        setIsLoggedIn(false);
        setUser(null);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [navigate, location, setIsLoggedIn, setUser]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      {error ? (
        <p style={{ color: 'red' }}>Error: {error}</p>
      ) : (
        <p>Authenticating with Google...</p>
      )}
    </div>
  );
}

export function AppleOAuthCallback(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const { setIsLoggedIn, setUser } = useAuth();

  useEffect(() => {
    const handleCallback = async (): Promise<void> => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');
      
      if (!code) {
        console.error('No authorization code received');
        setError('No authorization code received');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        await api.auth.login(code, 'apple');
        // Fetch user data after successful login
        const userData = await api.user.get();
        setUser(userData);
        setIsLoggedIn(true);
        
        // Process redirect based on state parameter
        const redirectUrl = processRedirectState(state);
        navigate(redirectUrl);
      } catch (error) {
        console.error('Authentication failed:', error);
        const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
        setError(errorMessage);
        setIsLoggedIn(false);
        setUser(null);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [navigate, location, setIsLoggedIn, setUser]);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      {error ? (
        <p style={{ color: 'red' }}>Error: {error}</p>
      ) : (
        <p>Authenticating with Apple...</p>
      )}
    </div>
  );
} 